<template>
  <main class="content checkout-page">
    <div class="container">
      <div
        v-if="shop && transactionDetails && transactionDetails.qr && !showLoader"
        class="twint-payment"
      >
        <h1 class="twint-payment__title">{{ $t('cs.front.twint_title') }}</h1>
        <p v-if="transactionDetails.amount" class="twint-payment__amount">
          {{ $filters.priceFormat(transactionDetails.amount, shop.currency) }}
        </p>

        <div v-if="!isMobile" class="twint-payment__qr">
          <img :src="transactionDetails.qr" alt="TWINT QR" class="fade-in" />
        </div>

        <div class="twint-payment__code">
          <p class="twint-payment__code-label">{{ $t('cs.front.twint_payment_code') }}</p>
          <p class="twint-payment__code-value">{{ transactionDetails.token }}</p>
          <button type="button" class="twint-payment__copy" @click="copyCode">
            {{ codeCopied ? $t('cs.front.twint_code_copied') : $t('cs.front.twint_copy_code') }}
          </button>
        </div>

        <ol v-if="isMobile" class="twint-payment__steps">
          <li>{{ $t('cs.front.twint_mobile_step_1') }}</li>
          <li>{{ $t('cs.front.twint_mobile_step_2') }}</li>
          <li>{{ $t('cs.front.twint_mobile_step_3', { code: transactionDetails.token }) }}</li>
          <li>{{ $t('cs.front.twint_return_step') }}</li>
        </ol>
        <ol v-else class="twint-payment__steps">
          <li>{{ $t('cs.front.twint_desktop_step_1') }}</li>
          <li>{{ $t('cs.front.twint_desktop_step_2') }}</li>
          <li>{{ $t('cs.front.twint_desktop_step_3') }}</li>
        </ol>

        <p class="twint-payment__note">{{ $t('cs.front.twint_close_note') }}</p>
        <p class="twint-payment__waiting">
          <span class="twint-payment__spinner"></span>{{ $t('cs.front.twint_waiting') }}
        </p>

        <template v-if="isMobile">
          <button type="button" class="twint-payment__toggle" @click="showQr = !showQr">
            {{ showQr ? $t('cs.front.twint_hide_qr') : $t('cs.front.twint_show_qr') }}
          </button>
          <div v-if="showQr" class="twint-payment__qr">
            <img :src="transactionDetails.qr" alt="TWINT QR" />
          </div>
        </template>
      </div>
    </div>
  </main>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { useRoute } from 'vue-router';
import * as paymentMethodTypes from '../../../consts/payment_method';

export default {
  data() {
    return {
      paymentMethodTypes,
      countdown: 15,
      countdownInterval: null,
      paymentProcessing: false,
      txId: null,
      // A phone pays in the TWINT app on the same device, so the code comes first there, the QR code on a computer
      isMobile: window.matchMedia('(hover: none) and (pointer: coarse)').matches,
      showQr: false,
      codeCopied: false,
    };
  },
  computed: {
    ...mapGetters({
      shop: 'shop/selected',
      transactionDetails: 'transaction/selectedDetails',
      paymentStatus: 'transaction/paymentStatus',
      showLoader: 'showLoader',
    }),
  },
  methods: {
    ...mapActions('transaction', {
      fetchPaymentTransactionDetails: 'fetchTxDetails',
      processPayment: 'processPayment',
    }),
    async checkPayment(txId) {
      if (!txId || this.paymentProcessing) {
        return;
      }

      this.paymentProcessing = true;

      try {
        await this.processPayment(txId);

        if (this.paymentStatus) {
          this.stopChecking();
          this.$router.push({ name: 'checkout.thanks', params: { number: this.paymentStatus.number } });
        }
      } finally {
        this.paymentProcessing = false;
      }
    },
    startChecking(txId) {
      this.countdownInterval = setInterval(() => {
        this.countdown -= 1;

        if (this.countdown === 0) {
          this.countdown = 15;
          this.checkPayment(txId);
        }
      }, 1000);
    },
    stopChecking() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
    },
    copyCode() {
      if (!navigator.clipboard) {
        return;
      }

      navigator.clipboard.writeText(String(this.transactionDetails.token)).then(() => {
        this.codeCopied = true;
        setTimeout(() => { this.codeCopied = false; }, 3000);
      }).catch(() => {});
    },
    // On a phone the customer pays in the TWINT app, check at once when the page is visible again
    onVisibilityChange() {
      if (document.visibilityState === 'visible' && this.countdownInterval) {
        this.countdown = 15;
        this.checkPayment(this.txId);
      }
    },
  },
  async mounted() {
    const route = useRoute();
    const { txId } = route.params;

    this.txId = txId;
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.$store.commit('showLoader');

    try {
      if (!txId) {
        return;
      }

      await this.fetchPaymentTransactionDetails(txId);
      await this.checkPayment(txId);

      if (!this.paymentStatus) {
        this.startChecking(txId);
      }
    } finally {
      this.$store.commit('hideLoader');
    }

    this.$store.commit('hideSideCart');
  },
  beforeUnmount() {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.stopChecking();
  },
};
</script>

<style scoped>
.twint-payment {
  max-width: 28rem;
  margin: 0 auto;
  padding: 1.5rem 0 3rem;
}

.twint-payment__title {
  margin: 0;
  font-size: 1.5rem;
}

.twint-payment__amount {
  margin: 0.25rem 0 1.25rem;
  font-size: 1.125rem;
  color: #555;
}

.twint-payment__qr {
  margin: 0 0 1.25rem;
  text-align: center;
}

.twint-payment__qr img {
  width: 200px;
  height: 200px;
  image-rendering: pixelated;
}

.twint-payment__code {
  margin: 0 0 1.25rem;
  padding: 1rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  text-align: center;
}

.twint-payment__code-label {
  margin: 0;
  color: #555;
}

.twint-payment__code-value {
  margin: 0.25rem 0 0.75rem;
  font-size: 2.25rem;
  font-weight: bold;
  letter-spacing: 0.3em;
}

.twint-payment__copy,
.twint-payment__toggle {
  padding: 0.5rem 1rem;
  background: transparent;
  border: 1px solid #A3A3A3;
  border-radius: 2px;
  font-size: 1rem;
  cursor: pointer;
}

.twint-payment__toggle {
  width: 100%;
  margin: 0 0 1.25rem;
}

.twint-payment__steps {
  margin: 0 0 1.25rem;
  padding-left: 1.5rem;
  line-height: 1.5;
}

.twint-payment__steps li + li {
  margin-top: 0.5rem;
}

.twint-payment__note {
  margin: 0 0 1.25rem;
  padding: 0.75rem 1rem;
  background: #EEF6E6;
  border-radius: 4px;
  color: #3F6D14;
  line-height: 1.5;
}

.twint-payment__waiting {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 1.25rem;
  color: #555;
}

.twint-payment__spinner {
  flex: none;
  width: 1rem;
  height: 1rem;
  border: 2px solid #D9D9D9;
  border-top-color: #76B935;
  border-radius: 50%;
  animation: twint-spin 1s linear infinite;
}

@keyframes twint-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
